<script setup lang="ts">
import { useDisplay } from 'vuetify'

const display = ref(useDisplay())
const { $api } = useNuxtApp()

const { data } = await useAsyncData('home-v2', async () => {
  const [stats, competitions, activities, images, recentPosts, popularPosts] = await Promise.all([
    $api('stats/'),
    $api('competitions/competitions/?page_size=5&ordering=-id'),
    $api('performance/activities/?group_users=true&page_size=3'),
    $api('performance/activity-images/?page_size=3'),
    $api('blog/posts/?page_size=3'),
    $api('blog/posts/?page_size=3&featured=true&ordering=featured_order'),
  ])
  return {
    stats: stats as Stats,
    competitions: competitions.results as Array<Competition>,
    latestActivities: activities.results,
    images: images.results,
    recentPosts: recentPosts.results as Array<Post>,
    popularPosts: popularPosts.results as Array<Post>,
  }
})

useSeoMeta({
  title: 'SailingMetrics - GPS Sailing Analysis, Races & Regatta Management'
})

const personas = [
  {
    icon: 'mdi-sail-boat',
    color: 'blue',
    title: 'Recreational Sailor',
    description: 'Log your sails, track personal progress, and compete in community challenges. No racing experience needed.',
    features: [
      { icon: 'mdi-upload', text: 'Upload GPX tracks or Vakaros (VKX) files' },
      { icon: 'mdi-lightning-bolt', text: 'Join open challenges and see where you rank' },
      { icon: 'mdi-chart-line', text: 'Speed, VMG and route stats for every session' },
    ],
    cta: 'Upload a Track',
    ctaIcon: 'mdi-upload',
    ctaLink: '/upload',
  },
  {
    icon: 'mdi-trophy',
    color: 'amber-darken-2',
    title: 'Competitive Sailor',
    description: 'Race virtually with GPS scoring, dig into post-race analytics, and climb the league rankings.',
    features: [
      { icon: 'mdi-cellphone', text: 'Create app-tracked races on any course' },
      { icon: 'mdi-compare', text: 'Overlay tracks to find where time was lost' },
      { icon: 'mdi-medal-outline', text: 'League standings updated after every race' },
    ],
    cta: 'Join the League',
    ctaIcon: 'mdi-trophy',
    ctaLink: '/league',
  },
  {
    icon: 'mdi-flag-checkered',
    color: 'green-darken-1',
    title: 'Regatta Organiser',
    description: 'Run professional regattas with live tracking, race timing, and automated handicap scoring.',
    features: [
      { icon: 'mdi-timer-outline', text: 'Start sequences and finish line time capture' },
      { icon: 'mdi-map-marker-path', text: 'Live location streaming for all competitors' },
      { icon: 'mdi-calculator-variant-outline', text: 'Yardstick, Portsmouth, PHRF and correction-factor scoring' },
    ],
    cta: 'Host a Regatta',
    ctaIcon: 'mdi-flag-checkered',
    ctaLink: '/competitions',
  },
  {
    icon: 'mdi-account-supervisor',
    color: 'deep-purple',
    title: 'Sailing Coach',
    description: 'Link up with your students, follow their progress session by session, and analyse sailing video to pinpoint technique improvements.',
    features: [
      { icon: 'mdi-account-multiple-outline', text: 'Track every student\'s sessions and trends in one dashboard' },
      { icon: 'mdi-compare', text: 'Overlay multiple sailors on the same map' },
    ],
    cta: 'Explore Coaching',
    ctaIcon: 'mdi-account-supervisor',
    ctaLink: '/coaching',
  },
]

// Quick links to per-role landing pages, shown at the bottom of the homepage. Add more entries
// here as those pages get built (e.g. For Regatta Organisers, For Competitive Sailors) — only
// Coaching exists today.
const roleLinks = [
  { label: 'For Coaches', to: '/coaching' },
]

const appScreenshots = [
  '/images/app/appstore_hero_6.5in.PNG',
  '/images/app/appstore_live-metrics_6.5in.PNG',
  '/images/app/appstore_tracking_6.5in.PNG',
]

const statsItems = [
  { value: 'n_sailors', label: 'Sailors' },
  { value: 'n_competitions', label: 'Competitions' },
  { value: 'recorded_hours', label: 'Hours Recorded' },
]
</script>

<template>
  <div>

    <!-- ── HERO ── -->
    <v-container class="pt-6 pb-10">
      <v-row align="center">
        <v-col cols="12" lg="6">
          <h1 class="text-h3 font-weight-bold mb-4" style="line-height: 1.15;">
            The Complete Platform<br>for Sailing Performance
          </h1>
          <p class="text-h6 text-medium-emphasis mb-6" style="font-weight: 400;">
            From your first GPS upload to race-winning analytics, SailingMetrics supports every sailor, coach, and regatta organiser. Free to start.
          </p>
          <div class="d-flex flex-wrap ga-3 mb-6">
            <v-btn size="x-large" color="primary" href="/account/signup/" nuxt>
              Get Started Free
            </v-btn>
            <v-btn size="x-large" variant="outlined" color="primary" href="#personas">
              Find Your Use Case
            </v-btn>
          </div>
          <SocialProof action="Join" :n-sailors="data?.stats.n_sailors" :show-avatars="false" />
        </v-col>
        <v-col cols="12" lg="6" class="d-none d-lg-flex justify-center align-center">
          <v-carousel id="hero-carousel" hide-delimiters height="420" style="border-radius: 8px; overflow: hidden;">
            <v-carousel-item
              v-for="img in data?.images"
              :src="img.thumbnail"
              :alt="`${img?.activity?.user_details?.name}, ${img?.activity?.venue_details?.name}`"
              cover
            >
              <nuxt-link class="text-decoration-none" :to="`/sails/${img?.activity?.id}`">
                <div class="d-flex pa-3 fill-height justify-center">
                  <v-sheet color="white" class="pa-2 align-self-end" rounded>
                    <span>{{ img?.activity?.user_details?.name }}, {{ img?.activity?.venue_details?.name }}</span>
                  </v-sheet>
                </div>
              </nuxt-link>
            </v-carousel-item>
          </v-carousel>
        </v-col>
      </v-row>
    </v-container>

    <!-- ── STATS BAR ── -->
    <v-container fluid class="accent text-white py-6">
      <v-row justify="center" class="text-center">
        <v-col cols="12" md="4" v-for="item in statsItems" :key="item.value">
          <div class="text-h4 font-weight-bold">{{ data?.stats[item.value] }}</div>
          <div class="text-body-1">{{ item.label }}</div>
        </v-col>
      </v-row>
    </v-container>

    <!-- ── PERSONAS ── -->
    <v-container id="personas" class="py-14">
      <v-row class="mb-8">
        <v-col class="text-center">
          <h2>Built for Every Sailor</h2>
          <p class="text-body-1 text-medium-emphasis mt-1">
            Whether you race, cruise, coach, or organise, find your tools below.
          </p>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" sm="6" lg="3" v-for="persona in personas" :key="persona.title">
          <v-card class="fill-height d-flex flex-column">
            <v-card-text class="pa-5 flex-grow-1">
              <v-avatar :color="persona.color" variant="tonal" size="48" rounded="lg" class="mb-3">
                <v-icon :icon="persona.icon" size="26" />
              </v-avatar>
              <h3 class="text-subtitle-1 font-weight-bold mb-3">{{ persona.title }}</h3>
              <p class="text-body-2 text-medium-emphasis mb-4">{{ persona.description }}</p>
              <v-list density="compact" bg-color="transparent">
                <v-list-item
                  v-for="f in persona.features"
                  :key="f.text"
                  :prepend-icon="f.icon"
                  class="pl-0"
                >
                  <v-list-item-title class="text-body-2" style="white-space: normal;">{{ f.text }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-card-text>
            <v-card-actions class="pa-5 pt-0">
              <v-btn
                color="primary"
                variant="tonal"
                :to="persona.ctaLink"
                :prepend-icon="persona.ctaIcon"
                nuxt
                block
              >
                {{ persona.cta }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- ── ANALYTICS & COMPUTER VISION ── -->
    <v-container fluid class="bg-grey-lighten-3 py-14">
      <v-container>
        <v-row align="center" class="mb-8">
          <v-col cols="12" md="6">
            <v-chip color="primary" variant="tonal" class="mb-3" size="small" prepend-icon="mdi-eye-outline">
              Sailing Analytics
            </v-chip>
            <h2 class="mb-3">From GPS Track to Actionable Insights</h2>
            <p class="text-body-1 mb-4">
              Every session generates a full performance report. Overlay tracks from multiple sailors, analyse each tack, and let our computer-vision AI review your sailing video automatically.
            </p>
            <v-list density="compact" bg-color="transparent" class="mb-5">
              <v-list-item prepend-icon="mdi-replay" class="pl-0">Full animated race replay with speed heatmap</v-list-item>
              <v-list-item prepend-icon="mdi-compare" class="pl-0">Side-by-side track comparison across sailors</v-list-item>
              <v-list-item prepend-icon="mdi-rotate-3d-variant" class="pl-0">Automatic tack and gybe detection and scoring</v-list-item>
              <v-list-item prepend-icon="mdi-robot-outline" class="pl-0">AI computer vision for video analysis</v-list-item>
              <v-list-item prepend-icon="mdi-speedometer" class="pl-0">Speed, VMG, VMC and heel metrics per leg</v-list-item>
            </v-list>
            <v-btn color="primary" variant="tonal" to="/documentation" nuxt prepend-icon="mdi-book-open-outline">
              Explore the Analytics
            </v-btn>
          </v-col>
          <v-col cols="12" md="6">
            <video
              controls
              width="1000"
              height="563"
              preload="none"
              poster="/images/sailing-video-analytics-poster.webp"
              style="border-radius: 8px; width: 100%; height: auto;"
            >
              <source src="/videos/sailing-video-analytics.mp4" type="video/mp4" />
            </video>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="6">
            <v-img
              src="/images/activity-analysis.webp"
              :aspect-ratio="1400 / 1248"
              alt="GPS track with speed heatmap and sensor data timeline"
              rounded="lg"
              style="border: 1px solid rgba(0,0,0,0.08);"
            />
            <p class="text-caption text-medium-emphasis mt-2 text-center">
              GPS track with speed heatmap, sensor data timeline, and tacking metrics
            </p>
          </v-col>
          <v-col cols="12" md="6">
            <v-img
              src="/images/start-analysis.png"
              :aspect-ratio="2248 / 1338"
              alt="AI-generated start line and upwind coaching insights"
              rounded="lg"
              style="border: 1px solid rgba(0,0,0,0.08);"
            />
            <p class="text-caption text-medium-emphasis mt-2 text-center">
              AI-generated start line analysis and upwind coaching insights
            </p>
          </v-col>
        </v-row>
      </v-container>
    </v-container>

    <!-- ── THE APP ── -->
    <v-container class="py-14">
      <v-row align="center">
        <v-col cols="12" md="5">
          <v-chip color="primary" variant="tonal" class="mb-3" size="small" prepend-icon="mdi-cellphone">
            iOS &amp; Android
          </v-chip>
          <h2 class="mb-3">The SailingMetrics App</h2>
          <p class="text-body-1 mb-4">
            Your on-the-water performance coach. Track every tack and gybe, get live speed callouts, and log your GPS track and video, all from a waterproof phone mounted on your boat.
          </p>
          <v-list density="compact" bg-color="transparent" class="mb-5">
            <v-list-item prepend-icon="mdi-speedometer" class="pl-0">Live speed, VMG &amp; heel with audio callouts</v-list-item>
            <v-list-item prepend-icon="mdi-rotate-3d-variant" class="pl-0">Automatic tack &amp; gybe detection</v-list-item>
            <v-list-item prepend-icon="mdi-video" class="pl-0">On-board video recording synced to GPS track</v-list-item>
            <v-list-item prepend-icon="mdi-flag-checkered" class="pl-0">Start sequences and finish line timing</v-list-item>
            <v-list-item prepend-icon="mdi-wifi-off" class="pl-0">Offline mode, just upload over WiFi after sailing</v-list-item>
          </v-list>
          <div class="d-flex flex-wrap ga-3">
            <v-btn color="primary" to="/app" nuxt prepend-icon="mdi-cellphone">
              Get the App Now
            </v-btn>
            <v-btn
              variant="outlined"
              color="primary"
              href="https://play.google.com/store/apps/details?id=com.adagger.sailingmetrics"
              target="_blank"
              rel="noopener noreferrer"
              prepend-icon="mdi-google-play"
            >
              Google Play
            </v-btn>
            <v-btn
              variant="outlined"
              color="primary"
              href="https://apps.apple.com/app/sailingmetrics/id6761767585"
              target="_blank"
              rel="noopener noreferrer"
              prepend-icon="mdi-apple"
            >
              App Store
            </v-btn>
          </div>
        </v-col>
        <v-col cols="12" md="7" class="d-flex justify-center align-center">
          <v-carousel
            height="480"
            hide-delimiter-background
            show-arrows="hover"
            cycle
            interval="4000"
            style="max-width: 240px; border-radius: 16px; overflow: hidden; box-shadow: 0 4px 24px rgba(0,0,0,0.18);"
          >
            <v-carousel-item v-for="src in appScreenshots" :key="src" :src="src" cover />
          </v-carousel>
        </v-col>
      </v-row>
    </v-container>

    <!-- ── CHALLENGE & LEAGUE ── -->
    <v-container fluid class="bg-grey-lighten-3 py-14">
      <v-container>
        <v-row align="stretch">
          <v-col cols="12" md="4">
            <v-chip color="primary" variant="tonal" class="mb-3" size="small" prepend-icon="mdi-trophy">
              Free to Join
            </v-chip>
            <h2 class="mb-3">Race and Challenge Other Sailors</h2>
            <p class="text-body-1 mb-0">
              Compete from anywhere, on any boat. The Open Challenge and the League let you race
              other sailors with GPS scoring — no race officer, no start time, no entry fee.
            </p>
          </v-col>

          <v-col cols="12" sm="6" md="4">
            <v-card variant="tonal" color="primary" class="pa-4 fill-height d-flex flex-column">
              <div class="d-flex align-center ga-2 mb-2">
                <v-icon color="primary">mdi-lightning-bolt</v-icon>
                <span class="font-weight-bold">Open Challenge</span>
              </div>
              <p class="text-body-2 mb-4">
                No course, no start time. Every sail counts — upload or import a session and climb
                a leaderboard that rewards all-round sailing: area, speed, maneuvers, consistency.
              </p>
              <v-btn class="mt-auto align-self-start" color="primary" size="small" to="/challenges" nuxt>
                See Challenges
              </v-btn>
            </v-card>
          </v-col>

          <v-col cols="12" sm="6" md="4">
            <v-card variant="tonal" color="primary" class="pa-4 fill-height d-flex flex-column">
              <div class="d-flex align-center ga-2 mb-2">
                <v-icon color="primary">mdi-medal-outline</v-icon>
                <span class="font-weight-bold">League</span>
              </div>
              <p class="text-body-2 mb-4">
                The <nuxt-link to="/app" class="text-decoration-none font-weight-medium">app</nuxt-link>
                builds a course with a start line and marks. Sail the ~30-minute lap and your speed is
                GPS-scored on Portsmouth Yardstick against everyone else.
              </p>
              <v-btn class="mt-auto align-self-start" color="primary" size="small" to="/league" nuxt>
                View the League
              </v-btn>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </v-container>

    <!-- ── RECENT RECORDINGS ── -->
    <v-container class="py-10">
      <ActivityTiles
        v-if="data?.latestActivities"
        :activities="data.latestActivities"
        show-all-label="Show all recordings"
        show-signup-prompt
      />
    </v-container>

    <!-- ── COMPETITIONS ── -->
    <v-container fluid class="bg-grey-lighten-3 py-14">
      <v-container>
        <v-row align="center">
          <v-col cols="12" md="5">
            <v-chip color="primary" variant="tonal" class="mb-3" size="small" prepend-icon="mdi-flag-checkered">
              Regatta Organisers
            </v-chip>
            <h2 class="mb-3">Host or Join Sailing Events</h2>
            <p class="text-body-1 mb-4">
              Manage a full regatta from your phone: start sequences, finish line timing, live competitor tracking, and automated handicap scoring. Results are published instantly.
            </p>
            <v-list density="compact" bg-color="transparent" class="mb-5">
              <v-list-item prepend-icon="mdi-check" class="pl-0">Multiple start sequences and race classes</v-list-item>
              <v-list-item prepend-icon="mdi-check" class="pl-0">Live GPS tracking for spectators and coaches</v-list-item>
              <v-list-item prepend-icon="mdi-check" class="pl-0">Yardstick, Portsmouth, PHRF and correction-factor scoring</v-list-item>
              <v-list-item prepend-icon="mdi-check" class="pl-0">Export results to CSV</v-list-item>
            </v-list>
            <div class="d-flex flex-wrap ga-3">
              <v-btn color="primary" to="/competitions" nuxt prepend-icon="mdi-magnify">
                All Competitions
              </v-btn>
              <v-btn color="primary" variant="tonal" to="/competitions" nuxt prepend-icon="mdi-plus">
                Host a Regatta
              </v-btn>
            </div>
          </v-col>
          <v-col cols="12" md="7">
            <v-list bg-color="transparent">
              <v-list-item
                v-for="(item, index) in data?.competitions"
                :key="`comp-${item.id}`"
                :class="{ 'border-b': index < (data?.competitions?.length ?? 0) - 1 }"
              >
                <template v-slot:prepend>
                  <div style="min-width: 35px;">
                    <v-img
                      v-show="item.country"
                      :src="`/flags/${item.country_details?.iso3166.toLowerCase()}.svg`"
                      :alt="item.country_details?.name"
                      min-width="10"
                      max-width="25"
                      contain
                    />
                  </div>
                </template>
                <v-list-item-title>
                  <nuxt-link :to="{ name: 'competitions-competition___en', params: { competition: `${item.slug}-${item.id}` } }">
                    {{ item.name }}
                  </nuxt-link>
                </v-list-item-title>
                <v-list-item-subtitle>
                  {{ niceDate(item.start_date) }}<span v-show="item.start_date !== item.end_date"> – {{ niceDate(item.end_date) }}</span>
                  <span v-if="item?.venue">&nbsp;· {{ item.venue_details?.name }}</span>
                </v-list-item-subtitle>
              </v-list-item>
            </v-list>
          </v-col>
        </v-row>
      </v-container>
    </v-container>

    <!-- ── BLOG ── -->
    <v-container class="py-14">
      <v-row align="center" class="mb-2">
        <v-col>
          <h2>Sailing News &amp; Tips</h2>
          <p class="text-body-1 text-medium-emphasis mt-1">Guides, stories, and updates from the sailing community.</p>
        </v-col>
        <v-col cols="auto">
          <v-btn variant="tonal" color="primary" to="/blog" nuxt>All Articles</v-btn>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" md="6">
          <div class="text-h6 d-flex align-center ga-2 mb-2">
            <v-icon icon="mdi-clock-outline" color="primary" />
            Recent Articles
          </div>
          <div class="d-flex flex-column ga-4">
            <nuxt-link
              v-for="item in data?.recentPosts"
              :key="`recent-${item.id}`"
              :to="`/blog/${item.slug}-${item.id}`"
              class="d-flex ga-3 text-decoration-none"
            >
              <v-img
                v-if="item.preview_image_url"
                :src="item.preview_image_url"
                width="120"
                height="84"
                cover
                rounded="lg"
                class="flex-shrink-0"
              />
              <div>
                <div class="text-subtitle-1 font-weight-medium text-high-emphasis">{{ item.title }}</div>
                <div class="text-body-2 text-medium-emphasis">{{ truncate(stripHtml(item.teaser), 100) }}</div>
                <div class="text-caption text-medium-emphasis mt-1">
                  {{ niceDate(item.publish_date) }} &middot; <span class="text-primary">Read more</span>
                </div>
              </div>
            </nuxt-link>
          </div>
        </v-col>
        <v-col cols="12" md="6">
          <div class="text-h6 d-flex align-center ga-2 mb-2">
            <v-icon icon="mdi-fire" color="primary" />
            Most Popular
          </div>
          <div class="d-flex flex-column ga-4">
            <nuxt-link
              v-for="item in data?.popularPosts"
              :key="`popular-${item.id}`"
              :to="`/blog/${item.slug}-${item.id}`"
              class="d-flex ga-3 text-decoration-none"
            >
              <v-img
                v-if="item.preview_image_url"
                :src="item.preview_image_url"
                width="120"
                height="84"
                cover
                rounded="lg"
                class="flex-shrink-0"
              />
              <div>
                <div class="text-subtitle-1 font-weight-medium text-high-emphasis">{{ item.title }}</div>
                <div class="text-body-2 text-medium-emphasis">{{ truncate(stripHtml(item.teaser), 100) }}</div>
                <div class="text-caption text-medium-emphasis mt-1">
                  {{ niceDate(item.publish_date) }} &middot; <span class="text-primary">Read more</span>
                </div>
              </div>
            </nuxt-link>
          </div>
        </v-col>
      </v-row>
    </v-container>

    <!-- ── FINAL CTA ── -->
    <v-container fluid class="py-16 text-center" style="background: #0d47a1;">
      <h2 class="text-white mb-3 text-h4 font-weight-bold">Ready to Improve Your Sailing?</h2>
      <p class="text-white text-body-1 mb-8" style="opacity: 0.85;">
        Free to join. Works with your GPS device, dedicated instrument, or just a waterproof phone.
      </p>
      <div class="d-flex justify-center flex-wrap ga-4">
        <v-btn size="x-large" color="white" href="/account/signup/" nuxt>
          <span class="text-primary font-weight-bold">Sign Up Free</span>
        </v-btn>
        <v-btn
          size="x-large"
          variant="outlined"
          color="white"
          to="/app"
          nuxt
          prepend-icon="mdi-cellphone"
        >
          <span class="text-white">Get the App</span>
        </v-btn>
        <v-btn
          size="x-large"
          variant="outlined"
          color="white"
          to="/upload"
          nuxt
          prepend-icon="mdi-upload"
        >
          <span class="text-white">Upload a Track</span>
        </v-btn>
      </div>
    </v-container>

    <!-- ── ROLE LINKS ── -->
    <v-container class="py-6 text-center">
      <span class="text-body-2 text-medium-emphasis mr-2">Looking for something specific?</span>
      <v-btn
        v-for="link in roleLinks"
        :key="link.to"
        variant="text"
        color="primary"
        size="small"
        :to="link.to"
        nuxt
      >
        {{ link.label }}
      </v-btn>
    </v-container>

  </div>
</template>

<style scoped>
#hero-carousel :deep(img) {
  -webkit-clip-path: polygon(8% 0, 0 100%, 100% 100%, 100% 0%);
  clip-path: polygon(8% 0, 0 100%, 100% 100%, 100% 0%);
}
</style>
